import { useCallback } from 'react';
import { parsePastedContent, calculateOrderForParent } from '../utils/taskUtils';

/**
 * Hook for handling paste operations in task text
 * @param {Object} params - Configuration object
 * @param {Array} params.tasks - All tasks
 * @param {Function} params.setEditingTaskId - Function to set editing task ID
 * @param {Function} params.updateTaskText - Function to update task text
 * @param {Function} params.updateTaskStatus - Function to update task status
 * @param {Function} params.updateTaskDueDate - Function to update task due date
 * @param {Function} params.createTask - Function to create a new task
 */
export function useTaskPaste({
  tasks,
  setEditingTaskId,
  updateTaskText,
  updateTaskStatus,
  updateTaskDueDate,
  createTask
}) {
  const createTasksFromParsedContent = useCallback((parsedTasks, parentTaskId, parentTaskIdForSubtasks) => {
    if (parsedTasks.length === 0) return;
    
    const createdTasks = [];
    const taskStack = [];
    const lastTaskAtParent = new Map();
    
    for (let i = 0; i < parsedTasks.length; i++) {
      const parsedTask = parsedTasks[i];
      const { text, indentLevel, status, dueDate } = parsedTask;
      
      let taskParentId = parentTaskId;
      
      if (indentLevel > 0) {
        let foundParent = false;
        if (taskStack.length > 0) {
          for (let j = taskStack.length - 1; j >= 0; j--) {
            if (taskStack[j].indentLevel === indentLevel - 1) {
              taskParentId = taskStack[j].task.id;
              foundParent = true;
              break;
            }
          }
        }
        
        if (!foundParent && parentTaskIdForSubtasks !== null && parentTaskIdForSubtasks !== undefined) {
          taskParentId = parentTaskIdForSubtasks;
        }
      }
      
      let order;
      const parentKey = taskParentId === null ? 'null' : taskParentId.toString();
      
      const lastTask = lastTaskAtParent.get(parentKey);
      if (lastTask) {
        order = calculateOrderForParent(taskParentId, tasks, lastTask.id);
      } else {
        order = calculateOrderForParent(taskParentId, tasks);
      }
      
      const newTask = createTask(text, taskParentId, order, status, dueDate);
      if (newTask) {
        createdTasks.push(newTask);
        while (taskStack.length > 0 && taskStack[taskStack.length - 1].indentLevel >= indentLevel) {
          taskStack.pop();
        }
        taskStack.push({ task: newTask, indentLevel: indentLevel });
        lastTaskAtParent.set(parentKey, newTask);
      }
    }
    
    return createdTasks;
  }, [tasks, createTask]);

  const handleTaskTextPaste = useCallback((e, clientId) => {
    // Always use plain text only to prevent XSS from pasted HTML (e.g. script, img onerror)
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    const hasMultipleLines = pastedText.includes('\n') || pastedText.includes('\r');

    // Handle placeholder task - create real task when pasting multiple lines
    if (clientId === 'placeholder' || clientId === 'placeholder_client_key') {
      if (hasMultipleLines) {
        e.preventDefault();
        const parsedTasks = parsePastedContent(pastedText);
        if (parsedTasks.length > 0) {
          createTasksFromParsedContent(parsedTasks, null, null);
        }
        return;
      }
      // Single line: prevent default HTML paste and insert plain text only
      e.preventDefault();
      document.execCommand('insertText', false, pastedText);
      return;
    }

    if (hasMultipleLines) {
      e.preventDefault();

      const currentTask = tasks.find(t => t._reactKey === clientId);
      if (!currentTask) return;

      const parsedTasks = parsePastedContent(pastedText);

      if (parsedTasks.length === 0) return;

      const firstTask = parsedTasks[0];

      e.target.textContent = firstTask.text;

      updateTaskText(currentTask.id, firstTask.text); // Use database ID
      if (firstTask.status !== currentTask.status) {
        updateTaskStatus(currentTask.id, firstTask.status); // Use database ID
      }
      if (firstTask.dueDate !== null && firstTask.dueDate !== undefined) {
        updateTaskDueDate(currentTask.id, firstTask.dueDate); // Use database ID
      }

      if (parsedTasks.length > 1) {
        const parentTaskId = currentTask.parent_task_id || null;
        createTasksFromParsedContent(parsedTasks.slice(1), parentTaskId, currentTask.id); // Use database ID
      }

      setEditingTaskId(null);
      e.target.blur();
      return;
    }

    // Single line on existing task: prevent default HTML paste and insert plain text only
    e.preventDefault();
    document.execCommand('insertText', false, pastedText);
  }, [tasks, updateTaskText, updateTaskStatus, updateTaskDueDate, createTasksFromParsedContent, setEditingTaskId]);

  return {
    handleTaskTextPaste
  };
}
